import { json } from '@codemirror/lang-json';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from '@mui/joy';
import {
  Content as DialogContent,
  DialogOverlay,
  Portal as DialogPortal,
  Root as DialogRoot,
} from '@radix-ui/react-dialog';

import * as React from 'react';

import { Code, CodeCard, CodeDataEditor } from '@/components/Code/Code';
import { DatasetPickerV2 } from '@/components/DatasetPickerV2';
import {
  EditInputs,
  EditOutputs,
} from '@/components/ExampleCrudPane/ExampleCrudPane';
import { Switch } from '@/components/Switch/Switch';
import { useDatasets } from '@/hooks/useSwr';
import { jsonSchemaExtensions } from '@/utils/use-codemirror-tooltip-follow-mouse';
import { isUUID } from '@/utils/uuid';

import { GraphDatasetNode } from './GraphDatasetProvider';
import { getValidationErrors } from './GraphDatasetProvider.utils';

export const GraphDatasetEditModal = ({
  node,
  isOpen,
  onClose,
  updateNodes,
}: {
  node: GraphDatasetNode;
  isOpen: boolean;
  onClose: () => void;
  updateNodes?: (newNode: GraphDatasetNode) => void;
}) => {
  const [datasetPickerValue, setDatasetPickerValue] = React.useState<
    string | undefined
  >(node.datasetNameOrId);

  const datasets = useDatasets(
    datasetPickerValue != null
      ? isUUID(datasetPickerValue)
        ? { id: [datasetPickerValue] }
        : { name: datasetPickerValue }
      : null,
    undefined,
    { revalidateOnFocus: false }
  );

  const dataset = datasets.data?.find(
    (d) => d.id === datasetPickerValue || d.name === datasetPickerValue
  );

  const [inputData, setInputData] = React.useState(node.stateInput);
  const [outputData, setOutputData] = React.useState(node.stateUpdate);
  const [_showInputSchema, setShowInputSchema] = React.useState(false);
  const [_showOutputSchema, setShowOutputSchema] = React.useState(false);

  const showInputSchema =
    _showInputSchema && !!dataset?.inputs_schema_definition;
  const showOutputSchema =
    _showOutputSchema && !!dataset?.outputs_schema_definition;

  const handleConfirm = () => {
    updateNodes?.({
      ...node,
      datasetNameOrId: datasetPickerValue ?? node.datasetNameOrId,
      stateInput: inputData,
      stateUpdate: outputData,
    });

    onClose();
  };

  const hasSchemaError = React.useMemo(() => {
    // Find the matching dataset from the datasets list
    const matchingDataset = datasets.data?.find(
      (dataset) =>
        dataset.id === datasetPickerValue || dataset.name === datasetPickerValue
    );

    // If no dataset is selected or no schema is defined, there can't be a mismatch
    if (
      !matchingDataset?.inputs_schema_definition &&
      !matchingDataset?.outputs_schema_definition
    ) {
      return false;
    }

    // Validate inputs schema if it exists
    if (
      getValidationErrors(matchingDataset.inputs_schema_definition, inputData)
    ) {
      return true;
    }

    // Validate outputs schema if it exists
    if (
      getValidationErrors(matchingDataset.outputs_schema_definition, outputData)
    ) {
      return true;
    }

    return false;
  }, [datasetPickerValue, datasets, inputData, outputData]);

  return (
    <DialogRoot open={isOpen} onOpenChange={onClose}>
      <DialogPortal>
        <DialogOverlay className="fixed inset-0 z-50 bg-background opacity-50" />
        <DialogContent className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="relative m-auto flex max-h-[80vh] w-full max-w-[800px] flex-col rounded-xl border border-secondary bg-popover">
            <button
              type="button"
              onClick={onClose}
              className="absolute right-4 top-4 text-secondary hover:text-primary"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
            <div className="overflow-y-auto p-6 ">
              <div className="flex-grow">
                <div className="px-4">
                  <h2 className="mb-4 text-xl font-semibold">
                    Add Node '{node.name}' to Dataset
                  </h2>
                  <div className="mb-4">
                    <div className="mb-2 flex items-center justify-between">
                      <h3 className="text-md font-semibold">Dataset</h3>
                      {datasetPickerValue && !dataset && (
                        <div className="rounded-full bg-[rgba(var(--joy-palette-success-mainChannel)/0.1)] px-3 py-1 text-xs font-semibold text-[rgba(var(--joy-palette-success-mainChannel)/1)]">
                          New Dataset
                        </div>
                      )}
                      {hasSchemaError && (
                        <div className="flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-700">
                          Schema mismatch
                        </div>
                      )}
                    </div>
                    <DatasetPickerV2
                      value={datasetPickerValue ?? null}
                      className="w-full"
                      onChange={(datasetName) =>
                        setDatasetPickerValue(datasetName ?? undefined)
                      }
                    />
                  </div>

                  <div className="flex flex-col gap-6">
                    <div className="flex flex-col">
                      <div className="mb-2 flex items-center justify-between">
                        <div>
                          <h3 className="text-md font-semibold">Input</h3>
                        </div>
                        {dataset?.inputs_schema_definition && (
                          <Switch
                            checked={showInputSchema}
                            onChange={() =>
                              setShowInputSchema(!showInputSchema)
                            }
                            label="Show input schema"
                          />
                        )}
                      </div>
                      <div
                        className={
                          showInputSchema ? 'grid grid-cols-2 gap-4' : ''
                        }
                      >
                        <div
                          className={
                            showInputSchema ? 'col-span-1' : 'col-span-2'
                          }
                        >
                          {dataset?.inputs_schema_definition ? (
                            <CodeCard>
                              <CodeDataEditor
                                language="json"
                                value={inputData}
                                onChange={(newValue) => setInputData(newValue)}
                                extensions={jsonSchemaExtensions(
                                  dataset?.inputs_schema_definition
                                )}
                              />
                            </CodeCard>
                          ) : (
                            <EditInputs
                              inputs={JSON.stringify(inputData, null, 2)}
                              setInputs={(value) =>
                                setInputData(JSON.parse(value))
                              }
                              dataType={dataset?.data_type ?? 'kv'}
                            />
                          )}
                        </div>
                        {showInputSchema &&
                          dataset?.inputs_schema_definition && (
                            <div className="col-span-1">
                              <CodeCard>
                                <Code
                                  language={json()}
                                  readOnly={true}
                                  value={JSON.stringify(
                                    dataset.inputs_schema_definition,
                                    null,
                                    2
                                  )}
                                />
                              </CodeCard>
                            </div>
                          )}
                      </div>
                    </div>
                    <div className="flex flex-col">
                      <div className="mb-2 flex items-center justify-between">
                        <div>
                          <h3 className="text-md font-semibold">Output</h3>
                        </div>
                        {dataset?.outputs_schema_definition && (
                          <Switch
                            checked={showOutputSchema}
                            onChange={() =>
                              setShowOutputSchema(!showOutputSchema)
                            }
                            label="Show output schema"
                          />
                        )}
                      </div>
                      <div
                        className={
                          showOutputSchema ? 'grid grid-cols-2 gap-4' : ''
                        }
                      >
                        <div
                          className={
                            showOutputSchema ? 'col-span-1' : 'col-span-2'
                          }
                        >
                          {dataset?.outputs_schema_definition ? (
                            <CodeCard>
                              <CodeDataEditor
                                language="json"
                                value={outputData}
                                onChange={(newValue) => setOutputData(newValue)}
                                extensions={jsonSchemaExtensions(
                                  dataset?.outputs_schema_definition
                                )}
                              />
                            </CodeCard>
                          ) : (
                            <EditOutputs
                              outputs={JSON.stringify(outputData, null, 2)}
                              setOutputs={(value) =>
                                setOutputData(JSON.parse(value))
                              }
                              dataType={dataset?.data_type ?? 'kv'}
                            />
                          )}
                        </div>
                        {showOutputSchema &&
                          dataset?.outputs_schema_definition && (
                            <div className="col-span-1">
                              <CodeCard>
                                <Code
                                  language={json()}
                                  readOnly={true}
                                  value={JSON.stringify(
                                    dataset?.outputs_schema_definition,
                                    null,
                                    2
                                  )}
                                />
                              </CodeCard>
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4">
              <Button color="neutral" variant="outlined" onClick={onClose}>
                Cancel
              </Button>
              <Button color="primary" variant="solid" onClick={handleConfirm}>
                Confirm
              </Button>
            </div>
          </div>
        </DialogContent>
      </DialogPortal>
    </DialogRoot>
  );
};
